
<script setup>
import { watch, ref, computed, nextTick } from 'vue'
import router from '../router/routes';
import { onMounted, onUnmounted } from 'vue';
import { useBusinessPartnersStore } from '@/stores/businessPartners'
const businessPartnersStore = useBusinessPartnersStore();
import { useUserStore } from '@/stores/user';
const userStore = useUserStore();
import wholeWordIcon from '@/assets/imgs/icons/whole-word.svg';
import FilterDropdown from '@/components/FilterDropdown.vue';
import InlineSpinner from '@/components/InlineSpinner.vue';
import MultiSelectDropdown from '@/components/MultiSelectDropdown.vue';

// Stellt sicher, dass der Filter-State immer ein gültiges Grundobjekt hat,
// damit Zugriffe wie businessPartnerSalesPitchStatus.id nicht auf undefined laufen
const ensureFilterDefaults = () => {
    const sel = userStore.currentSelection?.businessPartnerList;
    if (!sel) return;
    if (!sel.filter) {
        sel.filter = {};
    }
    const sp = sel.filter.businessPartnerSalesPitchStatus;
    if (!sp || typeof sp !== 'object') {
        sel.filter.businessPartnerSalesPitchStatus = { ids: [-1], dateStart: null, dateEnd: null };
    } else if (!Array.isArray(sp.ids)) {
        // Migrate legacy single-id format
        const legacyId = sp.id;
        sel.filter.businessPartnerSalesPitchStatus = {
            ids: (legacyId && Number(legacyId) > 0) ? [Number(legacyId)] : [-1],
            dateStart: sp.dateStart || null,
            dateEnd: sp.dateEnd || null,
        };
    }
    if (!sel.filter.invoice || typeof sel.filter.invoice !== 'object') {
        sel.filter.invoice = { dateStart: null, dateEnd: null };
    }
};

ensureFilterDefaults();

// Deaktiviert alle Filtergruppen auf einmal
const disableAllGroups = () => {
    Object.keys(ui.value.sections).forEach(key => {
        ui.value.sections[key].enabled = false;
    });
    ui.value.searchVisible = false;
};

// --- Produktgruppen State für Umsatzfilter (wiederhergestellt) ---
const filterCollapsed = ref(false);
const toggleFilterCollapsed = () => {
    filterCollapsed.value = !filterCollapsed.value;
};
const productGroups = [
    { code: 'GS', label: 'Gelbe Seiten' },
    { code: 'TG', label: 'Telefonbuch' },
    { code: 'TB', label: 'Örtliche' },
    { code: 'OMP', label: 'OMP' },
    { code: 'SUM', label: 'SUM Gesamt' }
];
// 0 = inaktiv, 1 = aktiv (mit Umsatzfilter), 2 = beliebig (kein Umsatzfilter, aber als Gruppe berücksichtigt?)
const productGroupStates = ref({ GS: 2, TG: 2, TB: 2, OMP: 2, SUM: 2 });
// Per-PG Umsatzbereiche
const revenueRanges = ref({
    GS: { min: null, max: null },
    TG: { min: null, max: null },
    TB: { min: null, max: null },
    OMP: { min: null, max: null },
    SUM: { min: null, max: null }
});
// Legacy global min/max (weiter zur Validierung genutzt / kann entfernt werden wenn obsolet)
const revenueMin = ref(0);
const revenueMax = ref(5000);
const defaultRanges = {
    GS: [0, 5000],
    TG: [0, 5000],
    TB: [0, 5000],
    OMP: [0, 5000],
    SUM: [0, 5000]
};
const activeGroupCount = ref(0);
const recomputeActiveCount = () => {
    activeGroupCount.value = Object.values(productGroupStates.value).filter(v => v === 1).length;
};
const statusLabel = (state) => state === 1 ? 'Umsatz' : state === 2 ? 'Beliebig' : 'kein Umsatz';
const statusClass = (state) => state === 1 ? 'status-active' : state === 2 ? 'status-neutral' : 'status-inactive';
const cycleStatus = (code) => {
    const cur = productGroupStates.value[code];
    productGroupStates.value[code] = (cur + 1) % 3; // 0->1->2->0
    recomputeActiveCount();
};
const presetRangeFor = (code) => {
    if (!defaultRanges[code]) return;
    const [min, max] = defaultRanges[code];
    revenueMin.value = min;
    revenueMax.value = max;
    // Set also per-group range for convenience
    revenueRanges.value[code] = { min, max };
};
// Encode states to string for backend filter
const encodeStates = () => productGroups.map(pg => `${pg.code}:${productGroupStates.value[pg.code]}`).join(',');

// Compute grouped options for Segmente based on delivered segment names dynamically
const segmentOptionsWithGroups = computed(() => {
    const all = (userStore.filterOption?.businessPartnerLists || []).map(o => ({ ...o }));
    const parents = all.filter(o => !o.isList && typeof o.id === 'string' && String(o.id).includes('list:'));
    const children = all.filter(o => o.isList);
    const standalone = all.filter(o => !o.isList && !(typeof o.id === 'string' && String(o.id).includes('list:')));

    const pickChildrenForParent = (parent) => {
        if (!parent) return [];
        const ids = String(parent.id).split(',').map(s => s.trim());
        return children.filter(c => ids.includes(String(c.id)));
    };
    const clsFor = (name) => {
        const n = (name || '').toLowerCase();
        if (n.includes('tourenmaterial')) return 'hdr-tour';
        if (n.includes('google')) return 'hdr-google';
        if (n.includes('aufgaben') || n.includes('lead')) return 'hdr-tasks';
        return '';
    };

    const out = [];
    // Preserve original order of parents as provided by backend
    for (const parent of parents) {
        const title = parent.name || '';
        const kids = pickChildrenForParent(parent);
        out.push({ id: `__hdr_${title}`, name: title, __header: true, __class: clsFor(title) });
            out.push({ ...parent, name: `Alle: ${title}`, __parent: true });
        for (const kid of kids) out.push({ ...kid, __child: true });
    }
    // Any standalone options (segments without lists) go under "Weitere"
    if (standalone.length) {
        out.push({ id: '__hdr_other', name: 'Weitere', __header: true, __class: '' });
        out.push(...standalone);
    }
    return out;
});

// Grouped options for PLZ/Stadt: City header -> "Alle <City>" (parent) -> its zip codes
// Jede PLZ-ID erscheint global nur einmal, damit es keine doppelten v-for :key Werte gibt
// (z.B. 41564 bei Kaarst / Kaarst-Holzbüttgen / Kaarst-Vorst).
const cityZipOptionsWithGroups = computed(() => {
    const cities = userStore.filterOption?.businessPartnerCityZip || [];
    const out = [];
    const globalSeenZipIds = new Set(); // globale Dedup über alle Städte

    for (const city of cities) {
        const cityName = city.name || '';

        if (!Array.isArray(city.zipList) || city.zipList.length === 0) continue;

        // Nur PLZs übernehmen, die noch nicht in einer vorherigen Stadt erschienen sind
        const uniqueZips = [];
        const localSeen = new Set();
        for (const zip of city.zipList) {
            const key = String(zip.id);
            if (localSeen.has(key)) continue;   // Dedup innerhalb derselben Stadt
            localSeen.add(key);
            if (globalSeenZipIds.has(key)) continue; // Dedup cityübergreifend
            globalSeenZipIds.add(key);
            uniqueZips.push(zip);
        }

        if (uniqueZips.length === 0) continue; // Stadt hat keine einzigartigen PLZs → überspringen

        out.push({ id: `__hdr_city_${cityName}`, name: cityName, __header: true, __class: 'hdr-city' });

        if (uniqueZips.length > 1) {
            const allZipIds = uniqueZips.map(z => z.id).join(',');
            out.push({
                id: `__all_city_${city.id}`,
                name: `Alle: ${cityName}`,
                __parent: true,
                zipIds: allZipIds,
                zipList: uniqueZips,
                cityName,
                cityId: city.id
            });
        }

        for (const zip of uniqueZips) {
            out.push({
                ...zip,
                __child: true,
                cityName,
                cityId: city.id
            });
        }
    }

    if (!out.length) {
        out.push({ id: '__no_data', name: 'Keine PLZ-Daten für Suchbegriff', __header: true, __class: 'hdr-city' });
    }

    return out;
});

// --- Kontaktstatus Multiselect ---
const KEIN_AUSSENDIENST_ID = 11;
const IDS_WITH_DATE_RANGE = [2, 3, 5, 6, 7, 8, 9];

const toISODateLocal = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

// Sorted options: id=11 (kein Aussendienst) always first
const sortedKontaktstatusOptions = computed(() => {
    const opts = [
        ...(userStore.filterOption?.businessPartnerSalesPitchStatus ||
            userStore.filterOption?.SalesPitchStatus ||
            userStore.filterOption?.salesPitchStatus || [])
    ].filter(o => [1, 2, 3, 5, 6, 7, 8, 9, 11].includes(Number(o.id)));
    const kein = opts.find(o => Number(o.id) === KEIN_AUSSENDIENST_ID);
    const rest = opts.filter(o => Number(o.id) !== KEIN_AUSSENDIENST_ID);
    return kein ? [kein, ...rest] : rest;
});

const availableKontaktstatusIds = computed(() =>
    sortedKontaktstatusOptions.value.map(o => Number(o.id))
);

const getKontaktstatusDefaults = () => {
    const preferred = [1, 2, 3].filter(id => availableKontaktstatusIds.value.includes(id));
    if (preferred.length > 0) return preferred;
    return availableKontaktstatusIds.value.length > 0 ? [availableKontaktstatusIds.value[0]] : [];
};

// Array of selected status IDs (default [1, 2, 3] = Unbearbeitet + WVL + Termine)
const selectedKontaktstatusIds = computed({
    get() {
        const sp = userStore.currentSelection?.businessPartnerList?.filter?.businessPartnerSalesPitchStatus;
        if (Array.isArray(sp?.ids)) {
            return sp.ids.map(Number);
        }

        return [];
    },
    set(val) {
        const sp = userStore.currentSelection?.businessPartnerList?.filter?.businessPartnerSalesPitchStatus;
        if (!sp) return;
        sp.ids = val.map(Number);
    }
});

const visibleKontaktstatusIds = computed(() => {
    if (!availableKontaktstatusIds.value.length) return [];
    return selectedKontaktstatusIds.value.filter(id => availableKontaktstatusIds.value.includes(Number(id)));
});

// Is date range applicable to any of the selected IDs?
const isKeinAussendienstSelected = computed(() =>
    selectedKontaktstatusIds.value.includes(KEIN_AUSSENDIENST_ID)
);

const isDateRangeVisible = computed(() =>
    isKeinAussendienstSelected.value
    || selectedKontaktstatusIds.value.some(id => IDS_WITH_DATE_RANGE.includes(id))
);

const showSalesPitchQuickPicks = computed(() =>
    isDateRangeVisible.value && !isKeinAussendienstSelected.value
);

const isSalesPitchDateEndDisabled = computed(() => isKeinAussendienstSelected.value);

// A given option is the last selected one (cannot be deselected)
// const isLastSelected = (optId) =>
//     selectedKontaktstatusIds.value.length === 1 && selectedKontaktstatusIds.value.includes(Number(optId));

// Toggle a Kontaktstatus option with exclusive + minimum-one rules
// Label-Funktion für MultiSelectDropdown (bevorzugt listAs)
const kontaktstatusLabelKey = (option) => option?.listAs || option?.name || '';

// Neue Auswahl aus MultiSelectDropdown mit Exklusiv- und Minimum-1-Regel verarbeiten
const handleKontaktstatusChange = (newIds) => {
    const nums = (newIds || [])
        .map(Number)
        .filter(id => availableKontaktstatusIds.value.includes(id));

    const prev = selectedKontaktstatusIds.value;

    const added = nums.filter(id => !prev.includes(id));

    if (added.includes(KEIN_AUSSENDIENST_ID)) {
        selectedKontaktstatusIds.value = [KEIN_AUSSENDIENST_ID];
    } 
    else if (
        prev.includes(KEIN_AUSSENDIENST_ID) &&
        nums.length > 1
    ) {
        selectedKontaktstatusIds.value = nums.filter(
            id => id !== KEIN_AUSSENDIENST_ID
        );
    } 
    else {
        selectedKontaktstatusIds.value = nums;
    }

    const sp = userStore.currentSelection
        ?.businessPartnerList
        ?.filter
        ?.businessPartnerSalesPitchStatus;

    if (sp) {
        sp.id = nums.length ? nums[0] : -1;
    }
};

// Einzelnen Status aus der Pill-Liste entfernen (nicht das letzte)
const removeKontaktstatus = (id) => {
    selectedKontaktstatusIds.value =
        selectedKontaktstatusIds.value.filter(
            i => i !== Number(id)
        );

    const sp = userStore.currentSelection
        ?.businessPartnerList
        ?.filter
        ?.businessPartnerSalesPitchStatus;

    if (sp) {
        sp.id = selectedKontaktstatusIds.value.length
            ? selectedKontaktstatusIds.value[0]
            : -1;
    }
};

// Nur Backend-gelieferte Optionen zulassen; bei leerer Auswahl sinnvolle Defaults setzen
watch(
    [availableKontaktstatusIds, selectedKontaktstatusIds],
    ([availableIds, selectedIds]) => {
        const selected = Array.isArray(selectedIds) ? selectedIds.map(Number) : [];
        const filtered = selected.filter(id => availableIds.includes(id));
        let next = filtered;
        const changed =
            next.length !== selected.length ||
            next.some((id, idx) => id !== selected[idx]);
        if (changed) {
            selectedKontaktstatusIds.value = next;
        }
    },
    { immediate: true }
);

// Clear date range when no date-supporting ID is selected
watch(isDateRangeVisible, (visible) => {
    if (!visible) {
        const sp = userStore.currentSelection?.businessPartnerList?.filter?.businessPartnerSalesPitchStatus;
        if (sp) { sp.dateStart = null; sp.dateEnd = null; }
    }
});

// For "Kein Außendienst Kontakt seit": default start date = today - 90 days, end date visible but disabled
watch(isKeinAussendienstSelected, (isSelected, wasSelected) => {
    if (!isSelected || wasSelected) return;
    const sp = userStore.currentSelection?.businessPartnerList?.filter?.businessPartnerSalesPitchStatus;
    if (!sp) return;
    const d = new Date();
    d.setDate(d.getDate() - 90);
    sp.dateStart = toISODateLocal(d);
    sp.dateEnd = null;
});

const handleResetFilter = () => {
    userStore.resetBusinessPartnerListFilter(true);
    resetAllPg();
    businessPartnerSearch.value = '';
}
// Merk sich die zuletzt "bewusst" angewendete Filtersignatur,
// um wiederholtes Anwenden ohne Änderung abzufangen.
// Im Store abgelegt, damit der Wert auch nach erneuter Mounting-Phase erhalten bleibt.
const lastAppliedSignature = computed({
    get() {
        return userStore.currentSelection?.businessPartnerList?.lastAppliedSignature || null;
    },
    set(value) {
        if (!userStore.currentSelection?.businessPartnerList) return;
        userStore.currentSelection.businessPartnerList.lastAppliedSignature = value;
    }
});
const alreadyAppliedHint = ref(false);
const alreadyAppliedCountdown = ref(0); // sichtbare Sekundenanzeige
const alreadyAppliedProgress = ref(1);  // 1.0 -> 0.0 für Balken
let alreadyAppliedTimer = null;
let alreadyAppliedRemainingMs = 0;
const ALREADY_APPLIED_DURATION_MS = 5000; // 5 Sekunden

// Lokale Ladezustände: Listenladen & Laden der Filteroptionen
const isListLoading = computed(() => {
    return userStore.currentSelection?.businessPartnerList?.isLoading === true;
});

const isFilterOptionsLoading = computed(() => {
    return userStore.filterOptions?.isLoading === true;
});

const handleSubmitFilter = async () => {
        // ZUERST die Suche in den Store-Filter integrieren, damit
        // currentFilter (und damit die Signature) die Suche berücksichtigt.
        const term = (sanitizedInput.value || '').trim();
        if (term && isValidSearch.value) {
            userStore.currentSelection.businessPartnerList.filter.search = {
                mode: businessPartnerSearchMode.value,
                string: term,
                wholeWord: wholeWord.value ? 1 : 0
            };
        } else {
            delete userStore.currentSelection.businessPartnerList.filter.search;
        }

        // --- searchCriteria und view im Store aktualisieren, damit sie immer korrekt befüllt werden ---
        // currentFilter ist ein Pinia-Getter (kein Funktionsaufruf)
        if (userStore.currentFilter) {
            const { searchCriteria, view } = userStore.currentFilter;
            userStore.currentSelection.businessPartnerList.searchCriteria = searchCriteria;
            userStore.currentSelection.businessPartnerList.view = view;
        }

        // Aktuelle Filtersignatur ERST NACH Einbeziehen der Suche ermitteln
        // (für Doppel-Anwenden-Erkennung)
        let currentSignature = null;
        try {
            const currentFilterSnapshot = userStore.currentFilter;
            currentSignature = currentFilterSnapshot?.filter?.signature || null;
        } catch (e) {
            console.warn('[FILTER_SUBMIT] currentFilter not available', e);
            currentSignature = null;
        }

        // Wenn aktuelle Signatur identisch zur zuletzt angewendeten ist,
        // keinen erneuten Request auslösen, sondern nur einen Hinweis zeigen.
        if (currentSignature && currentSignature === lastAppliedSignature.value) {
            alreadyAppliedHint.value = true;
            // Countdown visuell anzeigen und nach Ablauf automatisch schließen
            if (alreadyAppliedTimer) {
                clearInterval(alreadyAppliedTimer);
            }
            alreadyAppliedRemainingMs = ALREADY_APPLIED_DURATION_MS;
            alreadyAppliedCountdown.value = Math.ceil(alreadyAppliedRemainingMs / 1000);
            alreadyAppliedProgress.value = 1;

            alreadyAppliedTimer = setInterval(() => {
                alreadyAppliedRemainingMs -= 100;
                if (alreadyAppliedRemainingMs <= 0) {
                    alreadyAppliedHint.value = false;
                    alreadyAppliedCountdown.value = 0;
                    alreadyAppliedProgress.value = 0;
                    clearInterval(alreadyAppliedTimer);
                    alreadyAppliedTimer = null;
                    return;
                }
                alreadyAppliedCountdown.value = Math.ceil(alreadyAppliedRemainingMs / 1000);
                alreadyAppliedProgress.value = alreadyAppliedRemainingMs / ALREADY_APPLIED_DURATION_MS;
            }, 100);
            return;
        }

        // Während eines laufenden Ladevorgangs oder beim Laden der Filteroptionen
        // kein weiteres Anwenden zulassen
        if (isListLoading.value || isFilterOptionsLoading.value) {
            return;
        }

        // Pflichtfeld-Check für Produktfamilie
        if (typeof produktKombiPflichtError !== 'undefined' && produktKombiPflichtError.value) {
            // Fehleranzeige, kein Submit
            return;
        }

        // Ab hier gilt: dieser Filterzustand wird angewendet → Signatur vorm Request merken
        if (currentSignature) {
            lastAppliedSignature.value = currentSignature;
        }
        userStore.currentSelection.businessPartnerList.page = 1;
        userStore.currentSelection.businessPartnerList.pages = 1;




        // Vor dem Request: Filter-Payload für das Backend korrekt aufbauen (id/include-Objekte)
        const produktKombiArr = (userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerProductSegment || []);
        const produkteArr = (userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerProduct || []);

        // Update the store's filter state with correct backend payload structure
        // IDs im Store belassen, Backend-Format nur temporär bauen
        // Backend-Payload ohne Include/Exclude-Objekte bauen
        // IDs exakt wie im Array, ohne Split
        function mapIds(arr, includeObj, excludeObj) {
            if (!Array.isArray(arr)) arr = [];
            const result = arr.map(rawId => {
                const id = typeof rawId === 'object' && rawId !== null ? rawId.id : rawId;
                return {
                    id,
                    include: !!includeObj[id] && !excludeObj[id]
                };
            });
            return result;
        }

        const backendFilter = {
            ...userStore.currentSelection.businessPartnerList.filter,
            idBusinessPartnerProductSegment: mapIds(produktKombiArr, produktKombiInclude.value, produktKombiExclude.value),
            idBusinessPartnerProduct: mapIds(produkteArr, produkteInclude.value, produkteExclude.value)
        };

        // Alle anderen Felder (z.B. idBusinessPartnerList, idBusinessPartnerPotential, ...) wieder auf den Originalwert setzen, falls mapIds sie verändert hat
        // (mapIds wird nur für Produktfamilie und Produkte verwendet)
        // Die Originalwerte aus dem Filter holen und zurückschreiben:
        backendFilter.idBusinessPartnerList = userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerList;
        backendFilter.idBusinessPartnerPotential = userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerPotential;
        backendFilter.idBusinessPartnerOrder = userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerOrder;
        backendFilter.idBusinessPartnerStatus = userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerStatus;
        backendFilter.idBusinessPartnerAboStatus = userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerAboStatus;
        // Fix: Branche und PLZ immer übernehmen, auch wenn leer oder -1
        // Fix: Wert immer exakt aus Options übernehmen
        const plzVal = userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerCityZip;
        const plzOption = cityZipOptionsWithGroups.value.find(opt => String(opt.id) === String(plzVal));
        backendFilter.idBusinessPartnerCityZip = plzOption ? plzOption.id : -1;
        const sectorVal = userStore.currentSelection.businessPartnerList.filter.nameBusinessPartnerSector;
        const sectorOption = (userStore.filterOption?.businessPartnerSector || []).find(opt => String(opt.name) === String(sectorVal));
        backendFilter.nameBusinessPartnerSector = sectorOption ? sectorOption.name : '';

        // Set Sortierrichtung nur über 'order' im Backend-Filter (ASC/DESC)
        if (!backendFilter.order) {
            backendFilter.order = 'DESC';
        }

        // Also set order in view.sort for backend compatibility
        if (userStore.currentSelection.businessPartnerList.view && userStore.currentSelection.businessPartnerList.view.sort) {
            userStore.currentSelection.businessPartnerList.view.sort.order = backendFilter.order;
        }

        // Navigation/Backend-Aufruf mit backendFilter (korrektes Payload-Format)
        // Falls der Router/Backend-Call das Filterobjekt braucht, hier backendFilter verwenden
        // Wichtig: backendFilter statt store.filter verwenden!
        // Wenn Produkte oder Produktfamilie gewählt sind, sende backendFilter als JSON, sonst nur die Signature
        const hasProducts = Array.isArray(userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerProduct) && userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerProduct.length > 0;
        const hasProductSegments = Array.isArray(userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerProductSegment) && userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerProductSegment.length > 0;

        // Setze den (für das Backend vorbereiteten) Filter explizit im Store,
        // damit userStore.currentFilter und Router-Guard mit der gleichen Struktur arbeiten.
        userStore.currentSelection.businessPartnerList.filter = backendFilter;
        if (userStore.currentFilter) {
            const { searchCriteria, view } = userStore.currentFilter;
            userStore.currentSelection.businessPartnerList.searchCriteria = searchCriteria;
            userStore.currentSelection.businessPartnerList.view = view;
        }

        try {
            // Beim expliziten Anwenden: nur Filter setzen und routen, KEIN getByFilter mehr hier!
            const targetFilter = hasProducts || hasProductSegments
                ? JSON.stringify(backendFilter)
                : userStore.currentFilter.filter.signature;
            const currentRoute = router.currentRoute.value;
            if (currentRoute.name !== 'BusinessPartnerList' || currentRoute.query.filter !== targetFilter) {
                await router.push({ name: 'BusinessPartnerList', query: { source: 'filter', filter: targetFilter, isUserFilter:true }});
            }
        } finally {
            // Nach dem Anwenden soll der Filter sichtbar/ geöffnet bleiben (Desktop & Mobile)
            if (userStore.currentSelection?.businessPartnerList?.filter) {
                userStore.currentSelection.businessPartnerList.filter.show = true;
                userStore.currentSelection.businessPartnerList.filter.isOpen = true;
                // UI-Sichtbarkeit für Branche und PLZ immer aktivieren, wenn Wert gesetzt
                const filter = userStore.currentSelection.businessPartnerList.filter;
                ui.value.sections.sector.enabled = true;
                ui.value.sections.cityZip.enabled = true;
                // Synchronisiere Wert im Filter mit Options
                const plzVal = backendFilter.idBusinessPartnerCityZip;
                userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerCityZip = plzVal;
                const sectorVal = backendFilter.nameBusinessPartnerSector;
                userStore.currentSelection.businessPartnerList.filter.nameBusinessPartnerSector = sectorVal;
                persistUiVisibilityToStore();
            }
            // Zuletzt angewendete Signatur nach erfolgreicher Navigation aktualisieren
            try {
                const updatedSnapshot = userStore.currentFilter;
                const updatedSig = updatedSnapshot?.filter?.signature || null;
                if (updatedSig) {
                    lastAppliedSignature.value = updatedSig;
                }
            } catch (e) {
                // stillschweigend ignorieren, falls currentFilter noch nicht bereit ist
            }
        }
    }

const closeFilter = () => {
    userStore.currentSelection.businessPartnerList.filter.isOpen = false;
}

onUnmounted(() => {
    if (alreadyAppliedTimer) {
        clearInterval(alreadyAppliedTimer);
        alreadyAppliedTimer = null;
    }
    alreadyAppliedRemainingMs = 0;
    alreadyAppliedCountdown.value = 0;
    alreadyAppliedProgress.value = 0;
});

// Collapsible UI state for sections
const ui = ref({
    sections: {
        // Initial: Status & Listen enabled; others off. Collapsible behavior removed; only enabled flag used.
        contactStatus: { enabled: true },
        customerStatus: { enabled: false },
        lists: { enabled: false },
        produkte: { enabled: false },
        segments: { enabled: false },
        sort: { enabled: true },
        productGroups: { enabled: false },
        potential: { enabled: false },
        cityZip: { enabled: false },
        sector: { enabled: false },
        invoice: { enabled: false }, // Faktura (neu hinzugefügt, initial deaktiviert)
    },
    searchVisible: true,
    searchEnabled: false
});

// UI‑Sichtbarkeitszustand der Filtergruppen im Store persistieren, damit er
// nach Routing/Neuladen genau so wiederhergestellt werden kann, wie der Nutzer
// ihn eingestellt hat (keine automatische Ausblendung nach Apply).
const persistUiVisibilityToStore = () => {
    const sel = userStore.currentSelection?.businessPartnerList;
    if (!sel) return;
    try {
        sel.uiSections = JSON.parse(JSON.stringify(ui.value.sections));
    } catch (e) {
        sel.uiSections = ui.value.sections;
    }
    sel.uiSearchVisible = !!ui.value.searchVisible;
};

const initUiVisibilityFromStore = () => {
    const sel = userStore.currentSelection?.businessPartnerList;
    if (!sel) return;
    if (sel.uiSections && typeof sel.uiSections === 'object') {
        // Bestehende Struktur beibehalten, aber gespeicherte enabled‑Flags übernehmen
        Object.keys(ui.value.sections).forEach((key) => {
            if (sel.uiSections[key] && typeof sel.uiSections[key] === 'object') {
                ui.value.sections[key].enabled = !!sel.uiSections[key].enabled;
            }
        });
    }
    if (typeof sel.uiSearchVisible === 'boolean') {
        ui.value.searchVisible = sel.uiSearchVisible;
    }
};

// Beim Start UI‑Zustand aus dem Store wiederherstellen und danach Änderungen
// immer zurück in den Store schreiben.
initUiVisibilityFromStore();
watch(
    () => ui.value.sections,
    () => { persistUiVisibilityToStore(); },
    { deep: true, immediate: true }
);
watch(
    () => ui.value.searchVisible,
    () => { persistUiVisibilityToStore(); },
    { immediate: true }
);

// Merkt sich, für welche Listen-Signatur die UI anhand der Summary zuletzt initialisiert wurde,
// damit Benutzeraktionen (Bubbles an/aus) nicht ständig von den Summary-Daten überschrieben werden.
const lastSummarySignature = ref(null);

// Wenn ein Segment (idBusinessPartnerList) im Filter gesetzt ist,
// soll die Segment-Sektion unabhängig vom Bubble-State sichtbar sein.
const hasActiveSegment = computed(() => {
    const f = userStore.currentSelection?.businessPartnerList?.filter;
    if (!f) return false;
    const id = f.idBusinessPartnerList;
    return id !== null && id !== undefined && id !== -1;
});

// Beim ersten Laden einer neuen Listen-Signatur die sichtbaren Filtergruppen
// automatisch anhand der Summary initialisieren: nur Bereiche einblenden, die
// auch in den Summary-Chips vorkommen. Danach bleibt der Zustand stabil und
// wird über uiSections im Store persistiert.
watch(
    () => ({
        sections: userStore.currentSelection?.businessPartnerList?.activeSummarySections,
        signature: userStore.currentSelection?.businessPartnerList?.signature,
    }),
    ({ sections, signature }) => {
        if (!sections || typeof sections !== 'object') return;
        if (!signature) return;
        const sel = userStore.currentSelection?.businessPartnerList;
        if (!sel) return;

        // Pro Signatur nur einmal automatisch initialisieren, damit spätere
        // manuelle Änderungen durch den Nutzer nicht überschrieben werden.
        if (lastSummarySignature.value === signature) return;

        // Wenn es bereits einen explizit gespeicherten UI-Zustand für diese
        // Liste gibt, diesen respektieren und nicht überschreiben.
        if (sel.uiSections && typeof sel.uiSections === 'object') {
            lastSummarySignature.value = signature;
            return;
        }

        // Alle bekannten Sections zunächst deaktivieren …
        Object.keys(ui.value.sections).forEach(key => {
            ui.value.sections[key].enabled = false;
        });
        // … und dann nur die laut Summary aktiven Bereiche einschalten.
        Object.entries(sections).forEach(([key, isActive]) => {
            if (!isActive) return;
            if (!ui.value.sections[key]) return;
            ui.value.sections[key].enabled = true;
        });

        // Suchbereich entsprechend der Summary sichtbar/unsichtbar schalten.
        ui.value.searchVisible = !!sections.search;

        lastSummarySignature.value = signature;
        persistUiVisibilityToStore();
    },
    { deep: true }
);

// Fallback: wenn ein Segment aktiv ist und es noch keinen gespeicherten
// UI-Zustand gibt, Segment-Gruppe sichtbar machen.
watch(
    () => ({
        idList: userStore.currentSelection?.businessPartnerList?.filter?.idBusinessPartnerList,
        show: userStore.currentSelection?.businessPartnerList?.filter?.show,
    }),
    ({ idList, show }) => {
        const sel = userStore.currentSelection?.businessPartnerList;
        if (!show) return;
        if (idList === null || idList === undefined || idList === -1) return;
        if (!sel || (sel.uiSections && typeof sel.uiSections === 'object')) return;
        if (ui.value.sections.segments) {
            ui.value.sections.segments.enabled = true;
        }
    },
    { immediate: true }
);

// Sortierrichtung-Helper: steuert filter.order (ASC/DESC) über einen Checkbox-Toggle
const sortOrderIsAsc = computed({
    get() {
        const order = userStore.currentSelection.businessPartnerList.filter.order;
        return String(order).toUpperCase() === 'ASC';
    },
    set(val) {
        userStore.currentSelection.businessPartnerList.filter.order = val ? 'ASC' : 'DESC';
    }
});
// Toggle enable state of filter sections (used by bubble buttons)
// Wichtig: nur die Sichtbarkeit steuern, Werte im Filter selbst NICHT zurücksetzen.
const toggleEnable = (sectionKey) => {
    const current = ui.value.sections[sectionKey]?.enabled ?? false;
    ui.value.sections[sectionKey] = { enabled: !current };
};
// --- Produktfamilie / Produkte Include/Exclude: bestehende Filter-Maps übernehmen ---
const initialBpFilter = userStore.currentSelection?.businessPartnerList?.filter || {};
const produktKombiInclude = ref(initialBpFilter.produktKombiInclude ? { ...initialBpFilter.produktKombiInclude } : {}); // { [id]: true }
const produktKombiExclude = ref(initialBpFilter.produktKombiExclude ? { ...initialBpFilter.produktKombiExclude } : {}); // { [id]: true }
// UI-Modell: immer nur reine IDs, egal ob Filter intern Objekte enthält
const selectedProduktKombis = computed({
    get() {
        const raw = userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerProductSegment || [];
        if (!Array.isArray(raw)) return [];
        return raw.map(v => (typeof v === 'object' && v !== null ? v.id : v));
    },
    set(val) {
        userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerProductSegment = Array.isArray(val) ? [...val] : [];
    }
});
// Set 'Nur' as default for each selected Produktfamilie
watch(selectedProduktKombis, (arr) => {
    if (!Array.isArray(arr)) return;
    arr.forEach(id => {
        if (!produktKombiInclude.value[id] && !produktKombiExclude.value[id]) {
            produktKombiInclude.value[id] = true;
        }
    });
    // Remove state for deselected
    Object.keys(produktKombiInclude.value).forEach(id => {
        if (!arr.includes(id)) delete produktKombiInclude.value[id];
    });
    Object.keys(produktKombiExclude.value).forEach(id => {
        if (!arr.includes(id)) delete produktKombiExclude.value[id];
    });
}, { immediate: true });

// Pflichtfeld: Mindestens "Nur" oder "Ausschließen" für jede Auswahl
const produktKombiPflichtError = computed(() =>
    selectedProduktKombis.value.some(id => !produktKombiInclude.value[id] && !produktKombiExclude.value[id])
);
// Die gewählten Produktfamilien (IDs) aus dem Dropdown
// Wenn ein Produkt in Include UND Exclude ist, hat Exclude Vorrang
const isProduktfamilieIncluded = (id) => !!produktKombiInclude.value[id] && !produktKombiExclude.value[id];
const isProduktfamilieExcluded = (id) => !!produktKombiExclude.value[id];
// Sync Filter-State für Backend
watch([produktKombiInclude, produktKombiExclude], () => {
    userStore.currentSelection.businessPartnerList.filter.produktKombiInclude = { ...produktKombiInclude.value };
    userStore.currentSelection.businessPartnerList.filter.produktKombiExclude = { ...produktKombiExclude.value };
}, { deep: true });

// --- Produkte Auswahl mit Include/Exclude ---
const produkteInclude = ref(initialBpFilter.produkteInclude ? { ...initialBpFilter.produkteInclude } : {}); // { [id]: true }
const produkteExclude = ref(initialBpFilter.produkteExclude ? { ...initialBpFilter.produkteExclude } : {}); // { [id]: true }
const selectedProdukte = computed({
    get() {
        const raw = userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerProduct || [];
        if (!Array.isArray(raw)) return [];
        return raw.map(v => (typeof v === 'object' && v !== null ? v.id : v));
    },
    set(val) {
        userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerProduct = Array.isArray(val) ? [...val] : [];
    }
});
watch(selectedProdukte, (arr) => {
    if (!Array.isArray(arr)) return;
    arr.forEach(id => {
        if (!produkteInclude.value[id] && !produkteExclude.value[id]) {
            produkteInclude.value[id] = true;
        }
    });
    Object.keys(produkteInclude.value).forEach(id => {
        if (!arr.includes(id)) delete produkteInclude.value[id];
    });
    Object.keys(produkteExclude.value).forEach(id => {
        if (!arr.includes(id)) delete produkteExclude.value[id];
    });
}, { immediate: true });
const produktePflichtError = computed(() => {
    const arr = Array.isArray(selectedProdukte.value) ? selectedProdukte.value : [];
    return arr.some(id => !produkteInclude.value[id] && !produkteExclude.value[id]);
});
const isProduktIncluded = (id) => !!produkteInclude.value[id] && !produkteExclude.value[id];
const isProduktExcluded = (id) => !!produkteExclude.value[id];
watch([produkteInclude, produkteExclude], () => {
    userStore.currentSelection.businessPartnerList.filter.produkteInclude = { ...produkteInclude.value };
    userStore.currentSelection.businessPartnerList.filter.produkteExclude = { ...produkteExclude.value };
}, { deep: true });

// Entfernt ein Produkt aus der Auswahl
function removeProdukt(id) {
    const arr = userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerProduct || [];
    userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerProduct = arr.filter(x => x !== id);
    delete produkteInclude.value[id];
    delete produkteExclude.value[id];
}

// ...existing code...
const toNullIfZero = (v) => {
    const n = Number(v);
    if (!isFinite(n) || isNaN(n)) return null;
    return n === 0 ? null : n;
};
const mapCodeToSalesKey = {
    GS: 'gs',
    TG: 'tb',    // Telefonbuch
    TB: 'oe',    // Örtliche
    OMP: 'omp',
    SUM: 'total'
};
// Inverse Mapping, um vorhandene salesValues aus dem Filter auf Produktgruppen zurückzuführen
const salesKeyToCode = {
    total: 'SUM',
    gs: 'GS',
    tb: 'TG',
    oe: 'TB',
    omp: 'OMP',
};

// Initialisierungs-Flag: während der ersten Synchronisation aus dem bestehenden Filter
// sollen Änderungen an productGroupStates/revenueRanges NICHT sofort salesValues im Store überschreiben.
const initializingSalesValues = ref(true);

// Bestehende salesValues aus dem aktuellen Filter in die lokale UI-State (productGroupStates, revenueRanges) übernehmen
const initSalesValuesFromFilter = () => {
    const selFilter = userStore.currentSelection?.businessPartnerList?.filter;
    const sv = selFilter && typeof selFilter.salesValues === 'object' ? selFilter.salesValues : null;
    if (!sv) {
        // Kein bestehender Umsatzfilter: Standardzustand belassen
        initializingSalesValues.value = false;
        // Stelle sicher, dass zumindest eine leere salesValues-Struktur existiert
        const empty = { total: null, gs: null, omp: null, oe: null, tb: null };
        if (!selFilter) return;
        if (!selFilter.salesValues) selFilter.salesValues = empty;
        return;
    }

    // Lokale Stati/Ranges zurücksetzen
    productGroupStates.value = { GS: 2, TG: 2, TB: 2, OMP: 2, SUM: 2 };
    revenueRanges.value = {
        GS: { min: null, max: null },
        TG: { min: null, max: null },
        TB: { min: null, max: null },
        OMP: { min: null, max: null },
        SUM: { min: null, max: null },
    };

    Object.entries(sv).forEach(([key, val]) => {
        const code = salesKeyToCode[key];
        if (!code) return;
        if (val === null || typeof val !== 'object') {
            // Beliebig
            productGroupStates.value[code] = 2;
            return;
        }
        const min = typeof val.min === 'number' ? val.min : null;
        const max = typeof val.max === 'number' ? val.max : null;
        if (min === 0 && max === 0) {
            // explizit kein Umsatz
            productGroupStates.value[code] = 0;
            revenueRanges.value[code] = { min: 0, max: 0 };
        } else {
            // aktiver Umsatzfilter
            productGroupStates.value[code] = 1;
            revenueRanges.value[code] = {
                min: min !== null ? min : 1,
                max: max,
            };
        }
    });

    recomputeActiveCount();
    // Globale Min/Max grob aus SUM übernehmen, falls vorhanden
    const sumRange = sv.total;
    if (sumRange && typeof sumRange === 'object') {
        revenueMin.value = typeof sumRange.min === 'number' ? sumRange.min : 0;
        revenueMax.value = typeof sumRange.max === 'number' ? sumRange.max : 5000;
    }

    initializingSalesValues.value = false;
};
const updateFilter = () => {
    recomputeActiveCount();
    // Während der Initialisierung aus einem bestehenden Filter salesValues nicht überschreiben
    if (initializingSalesValues.value) return;

    // Build salesValues object as required by backend using per-group ranges
    const salesValues = { total: null, gs: null, omp: null, oe: null, tb: null };
    for (const [code, state] of Object.entries(productGroupStates.value)) {
        const key = mapCodeToSalesKey[code];
        if (!key) continue;
        if (state === 1) { // Umsatz
            const range = revenueRanges.value[code] || { min: null, max: null };
            const minVal = Number(range.min ?? 1);
            const maxVal = range.max;
            salesValues[key] = { min: Math.max(1, minVal), max: toNullIfZero(maxVal) };
        } else if (state === 0) { // kein Umsatz
            salesValues[key] = { min: 0, max: 0 };
        } else { // Beliebig
            salesValues[key] = null;
        }
    }
    userStore.currentSelection.businessPartnerList.filter.salesValues = salesValues;
    // Remove legacy fields to avoid confusion
    delete userStore.currentSelection.businessPartnerList.filter.productGroupStates;
    delete userStore.currentSelection.businessPartnerList.filter.revenueMin;
    delete userStore.currentSelection.businessPartnerList.filter.revenueMax;
};
// Umsatz-Filter nur aktualisieren, wenn wir NICHT in der Initialisierungsphase sind
watch(productGroupStates, () => { if (!initializingSalesValues.value) updateFilter(); }, { deep: true });
watch([revenueMin, revenueMax], () => { if (!initializingSalesValues.value && activeGroupCount.value > 0) updateFilter(); });
watch(revenueRanges, () => { if (!initializingSalesValues.value) updateFilter(); }, { deep: true });
// Initial: vorhandene salesValues aus dem Filter übernehmen (falls vorhanden)
initSalesValuesFromFilter();
// Verbesserungen: Validierung, Bulk-Aktionen
const revenueRangeError = ref(false);
const validateRevenueRange = () => {
    if (activeGroupCount.value === 0) { revenueRangeError.value = false; return; }
    const min = Number(revenueMin.value);
    const max = Number(revenueMax.value);
    revenueRangeError.value = (isNaN(min) || isNaN(max) || min < 0 || max < 0 || min > max);
};
watch([revenueMin, revenueMax, activeGroupCount], validateRevenueRange);
validateRevenueRange();

const setAllActive = () => {
    productGroups.forEach(pg => productGroupStates.value[pg.code] = 1);
    presetRangeFor('SUM');
    updateFilter();
    validateRevenueRange();
};
const resetAllPg = () => {
    productGroups.forEach(pg => productGroupStates.value[pg.code] = 2);
    revenueMin.value = null;
    revenueMax.value = null;
    updateFilter();
    validateRevenueRange();
};

// --- Suchbereich (aus PreviewBusinessPartnerList übernommen) ---
const businessPartnerSearch = ref('');
const businessPartnerSearchMode = ref('name'); // 'GPNo' | 'name' | 'tel'
const wholeWord = ref(false);
const searchModeOptions = [
    { label: 'GPNo', value: 'GPNo' },
    { label: 'Name', value: 'name' },
    { label: 'Tel', value: 'tel' }
];
const searchPlaceholder = (mode) => {
    switch (mode) {
        case 'GPNo': return 'GP Nummer …';
        case 'tel': return 'Telefon …';
        case 'name':
        default: return 'Name / Firma …';
    }
};
const searchPlaceholderText = ref(searchPlaceholder(businessPartnerSearchMode.value));
watch(businessPartnerSearchMode, (m) => { searchPlaceholderText.value = searchPlaceholder(m); });
const sanitizedInput = ref('');
watch([businessPartnerSearch, businessPartnerSearchMode], ([term, mode]) => {
    if (['tel'].includes(mode)) sanitizedInput.value = (term || '').replace(/[^0-9+\s\-\/]/g, ''); // Nur erlaubte Zeichen für Tel-Modus zulassen: Ziffern, Leerzeichen, Bindestriche oder Schrägstriche
    else sanitizedInput.value = term || '';
}, { immediate: true });
watch(businessPartnerSearch, (val) => {
    if (/^[+0][0-9\s\-\/]*$/.test(val)) {
        // Auto-Switch auf tel  bei Input, der mit 0 oder '+' beginnt und sonst nur aus Ziffern, Leerzeichen, Bindestrichen oder Schrägstrichen besteht
        businessPartnerSearchMode.value = 'tel';
    } else if (/^([1-9]+|[a-zA-Z]{3}-?)[0-9]*$/.test(val)) {
        // Auto-Switch auf GPNo bei Input, der mit 3 Buchstaben + Bindestrich oder mit Ziffern von 1-9 beginnt und sonst nur aus Ziffern besteht
        businessPartnerSearchMode.value = 'GPNo';
    } else {
        businessPartnerSearchMode.value = 'name';
    }
});
const isValidSearch = ref(false);
watch([sanitizedInput, businessPartnerSearchMode], ([term, mode]) => {
    const t = (term || '').trim();
    if (mode === 'GPNo') isValidSearch.value = t.length >= 3; 
    else if (mode === 'tel') isValidSearch.value = t.length >= 6; 
    else isValidSearch.value = t.length >= 2;
}, { immediate: true });
const isSearching = ref(false);
const handleBusinessPartnerSearch = async () => {
    const term = (sanitizedInput.value || '').trim();
    if (!term || !isValidSearch.value || isSearching.value) return;
    const mode = businessPartnerSearchMode.value; // 'GPNo' | 'name' | 'tel'
    const wholeWordFlag = wholeWord.value ? 1 : 0;
    const searchString = term; // unverändert übergeben
    isSearching.value = true;
    try {
        // Integrierte Suche in getListByFilter: setze Filter.search und triggert normalen Filter-Submit
        userStore.currentSelection.businessPartnerList.filter.search = {
            mode,
            string: searchString,
            wholeWord: wholeWordFlag
        };
        await router.push({ name: 'BusinessPartnerList', query: { source: 'filter', filter: userStore.currentFilter.filter.signature } });
    } finally { isSearching.value = false; }
};
const clearSearch = () => { businessPartnerSearch.value = ''; };

// Steuert die Sichtbarkeit der Such-Sektion direkt anhand des tatsächlichen Suchbegriffs
// und synchronisiert Wert/Modus der Eingabefelder mit dem gespeicherten Filter.search,
// damit Suche + Modus nach "Anwenden" bestehen bleiben.
watch(
    () => userStore.currentSelection?.businessPartnerList?.filter?.search,
    (search) => {
        const hasSearch = !!(search && typeof search.string === 'string' && search.string.trim());
        ui.value.searchVisible = hasSearch;

        if (search && typeof search.string === 'string') {
            businessPartnerSearchMode.value = search.mode || 'name';
            businessPartnerSearch.value = search.string;
            const ww = search.wholeWord;
            wholeWord.value = (ww === 1 || ww === true || ww === '1' || ww === 'true' || ww === 'True');
        }
    },
    { deep: true, immediate: true }
);

// Enforce max 4 selections for Produktfamilie
watch(() => userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerProductSegment, (arr) => {
    if (!Array.isArray(arr)) return;
    if (arr.length > 4) {
        userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerProductSegment = arr.slice(0, 4);
    }
}, { deep: false });

// Enforce max 4 selections for Produkte
watch(() => userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerProduct, (arr) => {
    if (!Array.isArray(arr)) return;
    if (arr.length > 4) {
        userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerProduct = arr.slice(0, 4);
    }
}, { deep: false });

// Scroll-Ziel aus Summary-Chips: wenn gesetzt, zur passenden Filtergruppe scrollen
watch(
    () => userStore.currentSelection?.businessPartnerList?.scrollToFilterSection,
    async (section) => {
        if (!section) return;
        await nextTick();
        try {
            if (section === 'search') {
                const el = document.querySelector('.search-panel');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                const el = document.querySelector(`.filter-group--${section}`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        } finally {
            if (userStore.currentSelection?.businessPartnerList) {
                userStore.currentSelection.businessPartnerList.scrollToFilterSection = null;
            }
        }
    },
    { immediate: true }
);

// --- Period presets (KW, Monat, Quartal) ---
const setPeriodPreset = (target, type) => {
    const today = new Date();
    let start, end;
    const toISO = (d) => {
        // Format as local YYYY-MM-DD to avoid UTC timezone shifts
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };
    if (type === 'kw') {
        // ISO week: set to Monday of current week
        const day = today.getDay();
        const diffToMonday = (day === 0 ? -6 : 1 - day);
        start = new Date(today); start.setDate(today.getDate() + diffToMonday);
        end = new Date(start); end.setDate(start.getDate() + 6);
    } else if (type === 'month') {
        // Start at the first day of the current month (01.MM.YYYY) at local midnight
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        // End at the last day of the current month (inclusive end)
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
    } else if (type === 'quarter') {
        const qStartMonth = Math.floor(today.getMonth() / 3) * 3;
        // Start at the first day of the quarter (01.[qStartMonth]) at local midnight
        start = new Date(today.getFullYear(), qStartMonth, 1);
        start.setHours(0, 0, 0, 0);
        // End at last day of quarter (inclusive end)
        end = new Date(today.getFullYear(), qStartMonth + 3, 0);
        end.setHours(23, 59, 59, 999);
    } else {
        start = today; end = today;
    }
    if (target === 'salesPitch') {
        userStore.currentSelection.businessPartnerList.filter.businessPartnerSalesPitchStatus.dateStart = toISO(start);
        userStore.currentSelection.businessPartnerList.filter.businessPartnerSalesPitchStatus.dateEnd = toISO(end);
    } else if (target === 'invoice') {
        userStore.currentSelection.businessPartnerList.filter.invoice.dateStart = toISO(start);
        userStore.currentSelection.businessPartnerList.filter.invoice.dateEnd = toISO(end);
    }
};
</script>

<template>
    <div
        class="filter-wrapper"
        :class="{ 'collapsed': filterCollapsed, 'widget-wrapper': userStore.selectedMedia === 'desktop' }"
        :style="alreadyAppliedHint ? { overflowY: 'hidden' } : {}"
    >
        <div class="container-wrapper">
            <!-- Headline wrapper removed as requested -->
            <!-- Hinweis-Popup, wenn derselbe Filter erneut angewendet wird (unterhalb der Sticky-Filterbar) -->
            <div
                v-if="alreadyAppliedHint"
                class="filter-feedback-overlay"
                role="alertdialog"
                aria-modal="true"
                aria-label="Filter bereits aktiv"
            >
                <div class="filter-feedback-backdrop" @click="alreadyAppliedHint = false"></div>
                <div class="filter-feedback-modal">
                    <h3 class="filter-feedback-title">Filter bereits aktiv</h3>
                    <p class="filter-feedback-text">
                        Der aktuelle Filter ist bereits angewendet. Ändere zuerst eine Einstellung oder setze den Filter zurück, um die Liste erneut zu aktualisieren.
                    </p>
                    <div v-if="alreadyAppliedCountdown > 0" class="filter-feedback-timer">
                        <div class="filter-feedback-timer-bar">
                            <div
                                class="filter-feedback-timer-bar-fill"
                                :style="{ width: (alreadyAppliedProgress * 100) + '%' }"
                            ></div>
                        </div>
                        <span class="filter-feedback-timer-text">
                            Schließt automatisch in {{ alreadyAppliedCountdown }}&nbsp;Sekunden…
                        </span>
                    </div>
                    <button type="button" class="filter-feedback-btn" @click="alreadyAppliedHint = false">
                        Verstanden
                    </button>
                </div>
            </div>
            <div class="container">
                <!-- Sticky filter header placed at top-level so it stays for all sections -->
                    <div class="sticky-filterbar">
                        <h3 class="section-title small">Filtergruppen aktivieren</h3>
                        <div class="bubble-toggle-bar" role="group" aria-label="Filtergruppen aktivieren">
                            <div class="all-off-action">
                                <button type="button" class="all-off-link" @click="disableAllGroups">
                                    <span class="all-off-icon" aria-hidden="true">
                                        <!-- SVG Icon: Eye with slash (hide) -->
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-5 0-9-3.58-10-8a10.94 10.94 0 0 1 2.06-4.26"/><path d="M1 1l22 22"/><path d="M9.53 9.53a3.5 3.5 0 0 0 4.94 4.94"/><path d="M12 12v.01"/></svg>
                                    </span>
                                    <span class="all-off-text">Alle ausblenden</span>
                                </button>
                            </div>
                            <div class="bubble-group-row">
                                <button type="button" class="bubble" :class="{ active: ui.searchVisible }"
                                    @click="ui.searchVisible = !ui.searchVisible"
                                    :aria-pressed="ui.searchVisible.toString()">Suche</button>
                                <button type="button" class="bubble" :class="{ active: ui.sections.contactStatus.enabled }"
                                    @click="toggleEnable('contactStatus')">Kontaktstatus</button>
                                <button type="button" class="bubble" :class="{ active: ui.sections.customerStatus.enabled }"
                                    @click="toggleEnable('customerStatus')">Kundenstatus</button>
                                <button type="button" class="bubble" :class="{ active: ui.sections.lists.enabled }"
                                    @click="toggleEnable('lists')">Produktfamilien</button>
                                <button type="button" class="bubble" :class="{ active: ui.sections.produkte?.enabled }"
                                    @click="toggleEnable('produkte')">Produkte</button>
                                <button type="button" class="bubble" :class="{ active: ui.sections.segments.enabled || hasActiveSegment }"
                                    @click="toggleEnable('segments')">Segment</button>
                                <button type="button" class="bubble" :class="{ active: ui.sections.productGroups.enabled }"
                                    @click="toggleEnable('productGroups')">Umsätze</button>
                                <button type="button" class="bubble" :class="{ active: ui.sections.potential.enabled }"
                                    @click="toggleEnable('potential')">Potential</button>
                                <button type="button" class="bubble" :class="{ active: ui.sections.cityZip.enabled }"
                                    @click="toggleEnable('cityZip')">PLZ / Ort</button>
                                <button type="button" class="bubble" :class="{ active: ui.sections.sector.enabled }"
                                    @click="toggleEnable('sector')">Branche</button>
                                <button type="button" class="bubble" :class="{ active: ui.sections.invoice.enabled }"
                                    @click="toggleEnable('invoice')">Faktura</button>
                                <button type="button" class="bubble" :class="{ active: ui.sections.sort.enabled }"
                                    @click="toggleEnable('sort')">Sortierung</button>
                            </div>
                        </div>
                    </div>
                <div class="filter-content">
                    <!-- Wenn Filteroptionen laden: nur Hinweis anzeigen, restlichen Inhalt zurückhalten -->
                    <div
                        v-if="isFilterOptionsLoading"
                        class="filter-loading-banner filter-loading-banner--full"
                    >
                        <InlineSpinner size="sm" />
                        <span>Filteroptionen werden geladen…</span>
                    </div>
                    <!-- Search panel direkt unter Header nur, wenn Optionen geladen sind -->
                    <div class="search-panel" v-show="!isFilterOptionsLoading && ui.searchVisible">
                        <div class="search-line">
                            <select class="search-mode" v-model="businessPartnerSearchMode"
                                @change="handleBusinessPartnerSearch" aria-label="Suchmodus">
                                <option v-for="m in searchModeOptions" :key="m.value" :value="m.value">{{ m.label }}
                                </option>
                            </select>
                            <button type="button" class="search-flag" :class="{ active: wholeWord }"
                                @click="wholeWord = !wholeWord" :aria-pressed="wholeWord.toString()"
                                aria-label="Ganzes Wort suchen">
                                <img :src="wholeWordIcon" alt="Ganzes Wort" />
                            </button>
                        </div>
                        <div class="search-input-wrap">
                            <input class="search-input"
                                :inputmode="['GPNo', 'tel'].includes(businessPartnerSearchMode) ? 'numeric' : 'text'"
                                v-model="businessPartnerSearch" :placeholder="searchPlaceholderText"
                                @keyup.enter.prevent="handleBusinessPartnerSearch" autocomplete="off" />
                            <button v-if="businessPartnerSearch" type="button" class="search-clear" @click="clearSearch"
                                aria-label="Eingabe löschen">✕</button>
                        </div>
                        <div class="search-hint" v-if="ui.searchVisible && businessPartnerSearch && !isValidSearch">
                           <span>Mind. {{ businessPartnerSearchMode === 'name' ? '2' : (businessPartnerSearchMode === 'GPNo' ? '3' : '6') }} Zeichen für {{
                                businessPartnerSearchMode }}</span>
                        </div>
                    </div>
                    <!-- Kontaktstatus (Multiselect) -->
                    <!-- Kontaktstatus (Multiselect wie Produkte) -->
                    <div class="filter-group filter-group--contact" v-if="!isFilterOptionsLoading && ui.sections.contactStatus.enabled">
                        <div class="section-header static">
                            <span class="section-accent"></span>
                            <span>Kontaktstatus</span>
                        </div>
                        <div class="hint">
                            Wähle einen oder mehrere Status. „Kein Außendienst Kontakt" kann nur alleine angewendet werden.
                        </div>
                        <div class="section-body">
                            <MultiSelectDropdown
                                :model-value="selectedKontaktstatusIds"
                                :options="sortedKontaktstatusOptions"
                                :key-prop="'id'"
                                :label-key="kontaktstatusLabelKey"
                                :max-selected="sortedKontaktstatusOptions.length"
                                placeholder="Kontaktstatus wählen"
                                aria-label="Kontaktstatus"
                                @update:modelValue="handleKontaktstatusChange"
                                 />
                            <div v-if="visibleKontaktstatusIds.length" class="kontaktstatus-pill-list">
                                <div
                                    v-for="id in visibleKontaktstatusIds"
                                    :key="id"
                                    class="produkte-pill-row vertical"
                                    :class="{ 'kontaktstatus-exclusive-pill': Number(id) === KEIN_AUSSENDIENST_ID }"
                                >
                                    <span class="produkte-pill-label">
                                        {{ kontaktstatusLabelKey(sortedKontaktstatusOptions.find(o => Number(o.id) === Number(id))) }}
                                    </span>
                                    <div class="pill-btn-row">
                                        <button
                                            type="button"
                                            class="pill pill-exclude-toggle small"
                                            @click="removeKontaktstatus(id)"
                                        >Entfernen</button>
                                    </div>
                                </div>
                            </div>
                            <!-- Zeitraum Datepicker – nur sichtbar wenn Datums-relevante IDs ausgewählt sind -->
                            <div v-if="isDateRangeVisible" class="date-range vertical">
                                <div v-if="showSalesPitchQuickPicks" class="quick-picks">
                                    <button type="button" class="mini" @click="setPeriodPreset('salesPitch', 'kw')">KW</button>
                                    <button type="button" class="mini" @click="setPeriodPreset('salesPitch', 'month')">Monat</button>
                                    <button type="button" class="mini" @click="setPeriodPreset('salesPitch', 'quarter')">Quartal</button>
                                </div>
                                <div class="inputs">
                                    <input class="form-child" type="date"
                                        v-model="userStore.currentSelection.businessPartnerList.filter.businessPartnerSalesPitchStatus.dateStart"
                                        placeholder="Startdatum" />
                                    <input class="form-child date-end-field" type="date"
                                        v-model="userStore.currentSelection.businessPartnerList.filter.businessPartnerSalesPitchStatus.dateEnd"
                                        placeholder="Enddatum"
                                        :disabled="isSalesPitchDateEndDisabled" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Kundenstatus -->
                    <div class="filter-group filter-group--customer" v-if="!isFilterOptionsLoading && ui.sections.customerStatus.enabled">
                        <div class="section-header static">
                            <span class="section-accent"></span>
                            <span>Kundenstatus</span>
                        </div>
                        <div class="section-body">
                            <FilterDropdown
                                v-model="userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerStatus"
                                :options="userStore.filterOption?.businessPartnerStatus || userStore.filterOption?.status || []"
                                placeholder="Bitte Kundenstatus wählen"
                                aria-label="Kundenstatus" />
                        </div>
                    </div>
                    <!-- Produktfamilien (max. 4) -->
                    <div class="filter-group filter-group--segment" v-if="!isFilterOptionsLoading && ui.sections.lists.enabled">
                        <div class="section-header static">
                            <span class="section-accent"></span>
                            <span>Produktfamilien</span>
                        </div>
                        <div class="section-body">
                            <MultiSelectDropdown
                                v-model="selectedProduktKombis"
                                :options="userStore.filterOption?.businessPartnerProductSegment || []" :key-prop="'id'"
                                :label-key="'name'" :max-selected="4" placeholder="Produktfamilien wählen"
                                aria-label="Produktfamilien"
                                :option-disabled="option => selectedProduktKombis.length >= 4 && !selectedProduktKombis.includes(option.id)" />
                            <div v-if="selectedProduktKombis.length" class="produktkombi-include-exclude-list">
                                <!-- Pflichtfeldhinweis pro Zeile -->
                                <div v-for="id in selectedProduktKombis" :key="typeof id === 'object' && id !== null ? id.id : id" class="produktkombi-pill-row">
                                    <span class="produktkombi-pill-label">
                                        {{
                                            (userStore.filterOption?.businessPartnerProductSegment || []).find(o => o.id === (typeof id === 'object' && id !== null ? id.id : id))?.name
                                            || (typeof id === 'object' && id !== null ? id.id : id)
                                        }}
                                    </span>
                                    <div class="pill-btn-row">
                                        <button type="button" class="pill pill-include-toggle small"
                                            :class="{ active: isProduktfamilieIncluded(id) }"
                                            @click="if (!isProduktfamilieIncluded(id)) { produktKombiInclude[id] = true; produktKombiExclude[id] = false; }"
                                            :aria-pressed="isProduktfamilieIncluded(id).toString()"
                                            :disabled="isProduktfamilieIncluded(id)">Einschließen</button>
                                        <button type="button" class="pill pill-exclude-toggle small"
                                            :class="{ active: isProduktfamilieExcluded(id) }"
                                            @click="if (!isProduktfamilieExcluded(id)) { produktKombiExclude[id] = true; produktKombiInclude[id] = false; }"
                                            :aria-pressed="isProduktfamilieExcluded(id).toString()"
                                            :disabled="isProduktfamilieExcluded(id)">Ausschließen</button>
                                    </div>
                                </div>
                            </div>
                            <div class="hint">Wähle bis zu 4 Produktfamilien und markiere sie als "Einschließen" oder "Ausschließen".</div>
                            <div class="hint" :class="{ error: produktKombiPflichtError }">
                                <span v-if="produktKombiPflichtError">Für alle gewählten Produktfamilien muss "Einschließen" oder
                                    "Ausschließen"
                                    gesetzt werden.</span>
                            </div>
                        </div>
                    </div>

                    <!-- Produkte Filter: now a sibling, not nested -->
                    <div class="filter-group filter-group--product" v-if="!isFilterOptionsLoading && ui.sections.produkte.enabled">
                        <div class="section-header static">
                            <span class="section-accent"></span>
                            <span>Produkte</span>
                        </div>
                        <div class="section-body">
                            <MultiSelectDropdown
                                v-model="selectedProdukte"
                                :options="userStore.filterOption?.businessPartnerProduct.filter(o => o.useInBusinessPartnerFilter) || []"
                                :key-prop="'id'"
                                :label-key="'name'"
                                :max-selected="4"
                                placeholder="Produkte wählen"
                                aria-label="Produkte"
                                :option-disabled="option => selectedProdukte.length >= 4 && !selectedProdukte.includes(option.id)" />
                            <div v-if="selectedProdukte.length" class="produkte-include-exclude-list">
                                <div v-for="id in selectedProdukte" :key="typeof id === 'object' && id !== null ? id.id : id" class="produkte-pill-row vertical">
                                    <span v-if="!isProduktIncluded(id) && !isProduktExcluded(id)" class="pflicht-hint">Bitte wählen</span>
                                    <span class="produkte-pill-label">
                                        {{ (userStore.filterOption?.businessPartnerProduct || []).find(o => o.id === (typeof id === 'object' && id !== null ? id.id : id))?.name || (typeof id === 'object' && id !== null ? id.id : id) }}
                                    </span>
                                    <div class="pill-btn-row">
                                        <button type="button" class="pill pill-include-toggle small"
                                            :class="{ active: isProduktIncluded(id) }"
                                            @click="if (!isProduktIncluded(id)) { produkteInclude[id] = true; produkteExclude[id] = false; }"
                                            :aria-pressed="isProduktIncluded(id).toString()"
                                            :disabled="isProduktIncluded(id)">Einschließen</button>
                                        <button type="button" class="pill pill-exclude-toggle small"
                                            :class="{ active: isProduktExcluded(id) }"
                                            @click="if (!isProduktExcluded(id)) { produkteExclude[id] = true; produkteInclude[id] = false; }"
                                            :aria-pressed="isProduktExcluded(id).toString()"
                                            :disabled="isProduktExcluded(id)">Ausschließen</button>
                                    </div>
                                </div>
                            </div>
                            <div class="hint">Wähle bis zu 4 Produkte und markiere sie als "Einschließen" oder "Ausschließen".</div>
                            <div class="hint" :class="{ error: produktePflichtError }">
                                <span v-if="produktePflichtError">Für alle gewählten Produkte muss "Einschließen" oder "Ausschließen" gesetzt werden.</span>
                            </div>
                        </div>
                    </div>
                    <!-- Segmente: eigenständige Gruppe -->
                    <div class="filter-group filter-group--segments" v-if="!isFilterOptionsLoading && (ui.sections.segments.enabled || hasActiveSegment)">
                        <div class="section-header static">
                            <span class="section-accent"></span>
                            <span>Segment</span>
                        </div>
                        <div class="section-body">
                            <FilterDropdown
                                v-model="userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerList"
                                :options="segmentOptionsWithGroups" placeholder="Segment wählen"
                                aria-label="Segment" />
                        </div>
                    </div>
                    <div class="filter-group filter-group--sort" v-if="!isFilterOptionsLoading && ui.sections.sort.enabled">
                        <div class="section-header static">
                            <span class="section-accent"></span>
                            <span>Sortierung</span>
                        </div>
                        <div class="section-body">
                            <FilterDropdown
                                v-model="userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerOrder"
                                :options="userStore.filterOption?.businessPartnerOrder || []"
                                :disabled="!ui.sections.sort.enabled" placeholder="Sortierung wählen"
                                aria-label="Sortierung" />
                                <div class="sort-direction" v-if="ui.sections.sort.enabled">
                                    <span class="toggle-label">
                                        <label class="switch switch-text">
                                            <input type="checkbox" v-model="sortOrderIsAsc">
                                            <span class="slider round"></span>
                                        </label>
                                        <span class="toggle-state-label">
                                            {{ sortOrderIsAsc ? 'Aufsteigend' : 'Absteigend' }}
                                        </span>
                                    </span>
                                </div>
                        </div>
                    </div>

                    <div class="filter-group filter-group--sales" v-if="!isFilterOptionsLoading && ui.sections.productGroups.enabled">
                        <div class="section-header static">
                            <span class="section-accent"></span>
                            <span>Umsätze</span>
                        </div>
                        <div class="product-group-toggle-wrapper">
                            <!-- <p class="sub-head">Produktgruppen Umsatz</p> -->
                            <div class="pg-bulk-actions">
                                <button type="button" class="mini" @click="setAllActive">Alle aktiv</button>
                                <button type="button" class="mini" @click="resetAllPg">Zurücksetzen PG</button>
                            </div>
                            <div class="pg-rows">
                                <div class="pg-row" v-for="pg in productGroups" :key="pg.code">
                                    <div class="indicator-wrapper" @click="cycleStatus(pg.code)"
                                        :class="statusClass(productGroupStates[pg.code])"
                                        :title="`${pg.code} – Status: ${statusLabel(productGroupStates[pg.code])}`">
                                        <div class="pg-status-label">{{ statusLabel(productGroupStates[pg.code]) }}
                                        </div>
                                        <template v-if="pg.code === 'GS'">
                                            <img :class="{ 'active': productGroupStates.GS === 1 }"
                                                src="@/assets/imgs/logos/campaigns/Gelbe-Seiten-App-Icon.svg"
                                                alt="Abo in GS" />
                                        </template>
                                        <template v-else-if="pg.code === 'TG'">
                                            <img :class="{ 'active': productGroupStates.TG === 1 }"
                                                src="@/assets/imgs/logos/campaigns/Das-Telefonbuch-App-Icon.svg"
                                                alt="Abo im TB" />
                                        </template>
                                        <template v-else-if="pg.code === 'TB'">
                                            <img :class="{ 'active': productGroupStates.TB === 1 }"
                                                src="@/assets/imgs/logos/campaigns/Das-Oertliche-App-Icon.svg"
                                                alt="Abo im ÖTB" />
                                        </template>
                                        <template v-else-if="pg.code === 'OMP'">
                                            <div class="omp-label" :class="{ 'active': productGroupStates.OMP === 1 }">omp
                                            </div>
                                        </template>
                                        <template v-else>
                                            <div class="pg-fallback"
                                                :class="{ 'active': productGroupStates[pg.code] === 1 }">{{ pg.code }}
                                            </div>
                                        </template>
                                    </div>
                                    <div class="pg-inputs" :class="{ disabled: productGroupStates[pg.code] === 2 }">
                                        <input type="number" :min="productGroupStates[pg.code] === 1 ? 1 : 0" max="50000"
                                            step="50" v-model.number="revenueRanges[pg.code].min"
                                            :disabled="productGroupStates[pg.code] === 2"
                                            :readonly="productGroupStates[pg.code] === 0"
                                            :value="productGroupStates[pg.code] === 0 ? 0 : revenueRanges[pg.code].min"
                                            placeholder="Min" />
                                        <span class="sep">-</span>
                                        <input type="number" :min="productGroupStates[pg.code] === 1 ? 1 : 0" max="50000"
                                            step="50" v-model.number="revenueRanges[pg.code].max"
                                            :disabled="productGroupStates[pg.code] === 2"
                                            :readonly="productGroupStates[pg.code] === 0"
                                            :value="productGroupStates[pg.code] === 0 ? 0 : revenueRanges[pg.code].max"
                                            placeholder="Max (null für offen)" />
                                    </div>
                                </div>
                            </div>
                            <div class="hint error" v-if="revenueRangeError">Ungültiger Bereich (Min ≤ Max & ≥ 0)</div>
                            <div class="hint" v-else-if="activeGroupCount === 0">Mindestens eine Produktgruppe aktivieren
                            </div>
                            <div class="hint" v-else>Aktive PG: {{ activeGroupCount }}</div>
                            <!-- Legende am Ende der Produktgruppen-Sektion als Footer -->
                            <div class="pg-legend footer">
                                <span class="legend-item"><span class="legend-dot active"></span> Umsätze</span>
                                <span class="legend-item"><span class="legend-dot neutral"></span> Beliebig</span>
                                <span class="legend-item"><span class="legend-dot inactive"></span> keine Umsätze</span>
                            </div>
                        </div>
                    </div>
                    <!-- Potential -->
                    <div class="filter-group filter-group--potential" v-if="!isFilterOptionsLoading && ui.sections.potential.enabled">
                        <div class="section-header static"><span class="section-accent"></span><span>Potential</span></div>
                        <div class="section-body">
                            <FilterDropdown
                                v-model="userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerPotential"
                                :options="(userStore.filterOption?.businessPartnerPotential || []).filter(o => o.useInBusinessPartnerFilter)"
                                placeholder="Potential wählen" aria-label="Potential" />
                        </div>
                    </div>
                    <!-- PLZ / Ort -->
                    <div class="filter-group filter-group--cityZip" v-if="!isFilterOptionsLoading && ui.sections.cityZip.enabled">
                        <div class="section-header static"><span class="section-accent"></span><span>PLZ / Ort</span></div>
                        <div class="section-body">
                            <!-- Debug-Ausgabe entfernt -->
                            <FilterDropdown
                                v-model="userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerCityZip"
                                :options="cityZipOptionsWithGroups"
                                grouped-search
                                search-prop="name"
                                placeholder="PLZ / Ort wählen"
                                aria-label="PLZ / Ort"
                                @update:modelValue="val => { userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerCityZip = val ?? -1; }"
                            />
                        </div>
                    </div>
                    <!-- Branche -->
                    <div class="filter-group filter-group--sector" v-if="!isFilterOptionsLoading && ui.sections.sector.enabled">
                        <div class="section-header static"><span class="section-accent"></span><span>Branche</span></div>
                        <div class="section-body">
                            <!-- Debug-Ausgabe entfernt -->
                            <FilterDropdown
                                v-model="userStore.currentSelection.businessPartnerList.filter.nameBusinessPartnerSector"
                                :options="userStore.filterOption?.businessPartnerSector || []"
                                :key-prop="'name'"
                                :label-key="'name'"
                                :disabled="!ui.sections.sector.enabled"
                                placeholder="Bitte Branche wählen"
                                aria-label="Branche"
                                @update:modelValue="val => { userStore.currentSelection.businessPartnerList.filter.nameBusinessPartnerSector = val ?? ''; }"
                            />
                        </div>
                    </div>
                    <!-- Faktura -->
                    <div class="filter-group filter-group--invoice" v-if="!isFilterOptionsLoading && ui.sections.invoice.enabled">
                        <div class="section-header static"><span class="section-accent"></span><span>Faktura</span></div>
                        <div class="section-body">
                            <div class="date-range vertical">
                                <div class="quick-picks">
                                    <button type="button" class="mini"
                                        @click="setPeriodPreset('invoice', 'kw')">KW</button>
                                    <button type="button" class="mini"
                                        @click="setPeriodPreset('invoice', 'month')">Monat</button>
                                    <button type="button" class="mini"
                                        @click="setPeriodPreset('invoice', 'quarter')">Quartal</button>
                                </div>
                                <div class="inputs">
                                    <input class="form-child" type="date"
                                        v-model="userStore.currentSelection.businessPartnerList.filter.invoice.dateStart"
                                        placeholder="Startdatum" />
                                    <input class="form-child" type="date"
                                        v-model="userStore.currentSelection.businessPartnerList.filter.invoice.dateEnd"
                                        placeholder="Enddatum" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div> <!-- /filter-content -->
                <div class="sticky-filter-actions row actions">
                    <button @click="handleResetFilter">Zurücksetzen</button>
                    <button
                        @click="handleSubmitFilter"
                        :disabled="revenueRangeError || isFilterOptionsLoading"
                        :aria-disabled="(revenueRangeError || isFilterOptionsLoading).toString()"
                    >
                        Anwenden
                        <InlineSpinner
                            v-if="isListLoading"
                            size="sm"
                            class="apply-spinner"
                        />
                    </button>
                </div>
            </div> <!-- /container -->
        </div> <!-- /container-wrapper -->
    </div> <!-- /filter-wrapper -->
</template>

<style lang="scss" scoped>
    .bubble-group-row {
        display: flex;
        flex-wrap: wrap;
        gap: .4rem;
    }
.kontaktstatus-pill-list {
    margin-top: 0.4rem;
}
.kontaktstatus-exclusive-pill .produkte-pill-label {
    font-style: italic;
    color: #475569;
}
.filter-feedback {
    font-size: 0.8rem;
    color: #475569;
    margin-top: 0.3rem;
}
.filter-loading-banner {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.8rem;
    color: #475569;
    margin: 0.4rem 0 0.6rem;
}
/* Sticky filter actions at bottom */
.sticky-filter-actions {
    position: sticky;
    bottom: 0;
    z-index: 10;
    background: #fff;
    padding: .75rem .8rem;
    border-top: 1px solid #d8e1e6;
    box-shadow: 0 -2px 8px -2px rgba(0,0,0,.06);
    display: flex;
    gap: .65rem;
}

.sticky-filter-actions .actions button:last-child {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    color: #fff;
}

.apply-spinner {
    margin-left: 0.1rem;
}

.sticky-filter-actions .actions button:last-child span {
    font-size: 0.75rem !important;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: none;
    color: #fff !important;
}
/* Toggle Switch with Text Inside */
/* Toggle Switch with State Label on the Right */
/* Subtle Toggle Switch */
.switch {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 20px;
    margin: 0 8px;
}
.switch input {
    opacity: 0;
    width: 0;
    height: 0;
}
.slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #e9ecef;
    transition: background 0.3s;
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.04);
}
.slider:before {
    position: absolute;
    content: "";
    height: 14px;
    width: 14px;
    left: 3px;
    bottom: 3px;
    background-color: #fff;
    transition: .3s;
    border-radius: 50%;
    box-shadow: 0 1px 2px rgba(0,0,0,0.08);
    z-index: 1;
}
input:checked + .slider {
    background-color: #ff9800;
}
input:checked + .slider:before {
    transform: translateX(20px);
}
.toggle-label {
    display: flex;
    align-items: center;
    font-size: 0.95em;
    gap: 0.7rem;
}
.toggle-state-label {
    min-width: 90px;
    text-align: left;
    font-size: 1em;
    font-weight: 600;
    color: #222;
    transition: color 0.2s;
    user-select: none;
}
/* Pill-Label über Buttons für Produkte */
.produkte-pill-row.vertical,
.produktkombi-pill-row {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.2rem;
    margin-bottom: 0.5rem;
}
.produktkombi-pill-label {
    font-size: 0.85em;
    font-weight: 500;
    margin-bottom: 0.1em;
    word-break: break-word;
}
.pill-btn-row {
    display: flex;
    gap: 0.4rem;
}
.product-group-toggle-wrapper {
    margin: 0.75rem 0;
    padding: 0.75rem;
    border: 1px solid var(--border-color, #ddd);
    border-radius: 8px;
    background: var(--bg-secondary, #fafafa);
}

.product-group-toggle-wrapper .sub-head {
    font-weight: 600;
    margin: 0 0 0.5rem;
    font-size: 0.9rem;
}

.pg-bulk-actions {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
}

.pg-bulk-actions .mini {
    font-size: 0.6rem;
    padding: 0.3rem 0.5rem;
    cursor: pointer;
}

.pg-rows {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin: 24px 0;
}

.pg-row {
    display: grid;
    grid-template-columns: 88px 1fr;
    align-items: center;
    gap: 0.6rem;
}

.indicator-wrapper {
    position: relative;
    width: 64px;
    min-height: 60px;
    border-radius: 8px;
    background: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border: 1px solid #ccc;
    transition: all .2s;
    padding: 6px;
}

.indicator-wrapper img {
    max-width: 38px;
    max-height: 38px;
}

.indicator-wrapper .pg-fallback,
.indicator-wrapper .omp-label {
    font-weight: 600;
    font-size: 0.75rem;
}

.indicator-wrapper .pg-status-label {
    position: absolute;
    top: -0.9rem;
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.55rem;
    font-weight: 700;
    letter-spacing: .5px;
    text-transform: uppercase;
}

.indicator-wrapper.status-active {
    opacity: 1;
    border-color: #2d8f2d;
    box-shadow: 0 0 0 2px rgba(45, 143, 45, 0.25);
}

.indicator-wrapper.status-neutral {
    opacity: .55;
}

.indicator-wrapper:hover {
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.15);
}

.pg-legend {
    display: flex;
    gap: 0.75rem;
    margin: 0.4rem 0 0.6rem;
    flex-wrap: wrap;
    font-size: 0.55rem;
}

.pg-legend .legend-item {
    display: flex;
    align-items: center;
    gap: 0.25rem;
}

.pg-legend .legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 3px;
    display: inline-block;
    background: #ccc;
}

.pg-legend .legend-dot.active {
    background: #2d8f2d;
    opacity: 1;
}

.pg-legend .legend-dot.neutral {
    background: #888;
    opacity: .55;
}

.pg-legend .legend-dot.inactive {
    background: #b80000;
    opacity: 1;
}

.indicator-wrapper.status-inactive {
    opacity: .4;
    border-color: #b80000;
    box-shadow: 0 0 0 2px rgba(184, 0, 0, 0.25);
    filter: none;
}

.pg-legend.footer {
    margin-top: 1rem;
    padding-top: 0.5rem;
    border-top: 1px solid #ddd;
    display: flex;
    justify-content: space-around;
    font-size: 0.6rem;
}

.pg-inputs {
    display: flex;
    align-items: center;
    gap: 0.4rem;
}

.pg-inputs input {
    max-width: 140px;
    padding: 0.35rem 0.4rem;
    font-size: 0.75rem;
}

.pg-inputs .sep {
    font-size: 0.75rem;
    opacity: .7;
}

.pg-inputs.disabled {
    opacity: .4;
}

.hint {
    font-size: 0.6rem;
    opacity: .7;
}

.hint.error {
    color: #b80000;
    font-weight: 600;
}

/* --- Search styles aligned to Preview (neutral, no blue primary) --- */
.search-top {
    margin-bottom: 0.85rem;
}

.search-panel {
    display: flex;
    flex-direction: column;
    gap: .65rem;
    background: #fff;
    padding: .75rem .8rem;
    border: 1px solid #d2dde4;
    border-radius: .85rem;
    box-shadow: 0 4px 10px -2px rgba(0, 0, 0, .06);
    margin-top: .6rem;
}

.search-line {
    display: flex;
    gap: .55rem;
    align-items: center;
}

.search-toggle-bar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: .4rem;
}

.search-toggle {
    appearance: none;
    border: 1px solid #c3ced6;
    background: #eef3f6;
    color: #223;
    padding: .3rem .55rem;
    font-size: .7rem;
    border-radius: 999px;
    cursor: pointer;
    transition: .18s background, .18s border-color, .18s box-shadow, .18s color;
}

.search-toggle:hover {
    background: #e3eef3;
}

.search-toggle.active {
    background: rgba(var(--sellwerk-color-main-accent-rgb, 0, 143, 179), .10);
    border-color: var(--sellwerk-color-main-accent, #008fb3);
    box-shadow: 0 0 0 3px rgba(var(--sellwerk-color-main-accent-rgb, 0, 143, 179), .20);
}

.search-mode {
    appearance: none;
    background: #fff;
    border: 1px solid #c3ced6;
    padding: .5rem .7rem;
    font-size: .7rem;
    font-weight: 600;
    letter-spacing: .5px;
    text-transform: uppercase;
    border-radius: .55rem;
    min-width: 80px;
    cursor: pointer;
    transition: .18s;
}

.search-mode:hover {
    background: #f5fafc;
}

.search-mode:focus {
    outline: none;
    border-color: var(--sellwerk-color-main-accent, #008fb3);
    box-shadow: 0 0 0 3px rgba(var(--sellwerk-color-main-accent-rgb, 0, 143, 179), .25);
}

.search-flag {
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    width: 32px;
    border-radius: 50%;
    border: 1px solid #c3ced6;
    background: #fff;
    cursor: pointer;
    transition: .18s;
}

.search-flag img {
    width: 16px;
    height: 16px;
}

.search-flag:hover {
    background: #f0f6f9;
}

.search-flag.active {
    background: rgba(var(--sellwerk-color-main-accent-rgb, 0, 143, 179), .15);
    border-color: var(--sellwerk-color-main-accent, #008fb3);
    box-shadow: 0 0 0 3px rgba(var(--sellwerk-color-main-accent-rgb, 0, 143, 179), .25);
}

.search-input-wrap {
    position: relative;
    display: flex;
    align-items: center;
    border: 1px solid #c3ced6;
    border-radius: .65rem;
    padding: .15rem .15rem .15rem .6rem;
    background: #fff;
    transition: .2s border-color, .2s box-shadow;
}

.search-input-wrap:focus-within {
    border-color: var(--sellwerk-color-main-accent, #008fb3);
    box-shadow: 0 0 0 4px rgba(var(--sellwerk-color-main-accent-rgb, 0, 143, 179), .18);
}

.search-input {
    flex: 1;
    border: none;
    background: transparent;
    padding: .55rem .4rem;
    font-size: .8rem;
}

.search-input:focus {
    outline: none;
}

.search-clear {
    position: absolute;
    right: 3.5rem;
    top: 50%;
    transform: translateY(-50%);
    background: #b91c1c;
    border: none;
    color: #fff;
    font-size: .6rem;
    width: 18px;
    height: 18px;
    padding: 0;
    border-radius: 999px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: .18s;
}

.search-clear:hover {
    background: #dc2626;
}

.search-submit {
    background: var(--sellwerk-color-main-accent, #008fb3);
    color: #fff;
    border: none;
    padding: .55rem .9rem;
    border-radius: .55rem;
    font-size: .7rem;
    font-weight: 600;
    letter-spacing: .5px;
    cursor: pointer;
    transition: .2s background, .2s transform;
    margin-left: .4rem;
}

.search-submit:hover {
    background: rgba(var(--sellwerk-color-main-accent-rgb, 0, 143, 179), .85);
}

.search-submit:active {
    transform: scale(.94);
}

.search-submit:disabled {
    opacity: .35;
    cursor: not-allowed;
}

.search-hint {
    font-size: .6rem;
    letter-spacing: .5px;
    opacity: .75;
    padding: .1rem .25rem 0 .25rem;
}

.loader.tiny {
    width: 0.9rem;
    height: 0.9rem;
    border: 2px solid #fff;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin .8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.visually-hidden {
    position: absolute !important;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
}

/* Bubble toggles under search */
.bubble-toggle-bar {
    display: flex;
    flex-direction: column;
    gap: .4rem;
    margin: .6rem 0;
    .all-off-action {
        margin-bottom: 6px;
        display: flex;
        align-items: center;
    }
    .all-off-link {
        background: none;
        border: none;
        color: #b0b0b0;
        font-size: 0.85rem;
        font-weight: 400;
        display: flex;
        align-items: center;
        cursor: pointer;
        padding: 0;
        transition: color 0.2s;
    }
    .all-off-link:hover {
        color: #666;
        text-decoration: underline;
    }
    .all-off-icon {
        margin-right: 4px;
        display: flex;
        align-items: center;
    }
    .all-off-icon svg {
        width: 14px;
        height: 14px;
    }
    .all-off-text {
        font-size: 0.85rem;
    }
}

.bubble {
    appearance: none;
    border: none;
    cursor: pointer;
    padding: .35rem .6rem;
    border-radius: 999px;
    font-size: .75rem;
    line-height: 1;
    color: #223;
    background: #eef3f6;
    border: 1px solid #c3ced6;
    transition: .18s background, .18s border-color, .18s box-shadow, .18s color;
}

.bubble:hover {
    background: #e3eef3;
}

.bubble.active {
    color: #05303a;
    background: rgba(var(--sellwerk-color-main-accent-rgb, 0, 143, 179), .12);
    border-color: var(--sellwerk-color-main-accent, #008fb3);
    box-shadow: 0 0 0 2px rgba(var(--sellwerk-color-main-accent-rgb, 0, 143, 179), .20);
}

.filter-group.disabled {
    opacity: .55;
}

/* Wenn per scrollIntoView zu einer Gruppe gescrollt wird, soll die
    sticky Filter-Bar oben nichts überdecken: Reserve von ca. Bar-Höhe */
.filter-content .filter-group,
.search-panel {
	scroll-margin-top: 215px;
}

/* Sticky filter-group header */
.sticky-filterbar {
    position: sticky;
    top: 0;
    z-index: 10;
    background: #fff;
    padding: .75rem .8rem;
    border-bottom: 1px solid #d8e1e6;
}

.theme--dark .sticky-filterbar,
.sticky-filterbar.theme--dark {
    background: rgba(20, 23, 28, .96);
    border-bottom-color: rgba(255, 255, 255, 0.08);
}

/* Layout improvements */
.search-area {
    padding: 0.75rem 0 0.25rem;
    border-bottom: 1px solid #d8e1e6;
    margin-bottom: 0.75rem;
}

.bubble-area {
    margin-top: 0.65rem;
}

.section-title {
    font-size: .85rem;
    font-weight: 600;
    letter-spacing: .5px;
    margin: 0 0 .4rem;
    color: #123;
}

.section-title.small {
    font-size: .7rem;
    text-transform: uppercase;
    letter-spacing: .8px;
    opacity: .7;
}

.actions {
    margin-top: 1rem;
    display: flex;
    gap: .65rem;
}

.actions button {
    flex: 1;
    padding: .55rem .8rem;
    font-size: .75rem;
    border-radius: .6rem;
    border: 1px solid #c3ced6;
    cursor: pointer;
    background: #fff;
    transition: .18s background, .18s border-color;
}

.actions button:hover {
    background: #f0f6f9;
}

.actions button:last-child {
    background: var(--sellwerk-color-main-accent, #008fb3);
    color: #fff;
    border-color: transparent;
    transition: background 0.18s ease, transform 0.08s ease, border-color 0.18s ease;
}

.actions button:last-child:hover {
    /* Dezenter Hover: gleiche CI-Farbe, minimale Bewegung und helle Kontur */
    background: var(--sellwerk-color-main-accent, #008fb3);
    border-color: rgba(255, 255, 255, 0.7);
    transform: translateY(-0.5px);
}

/* Modern select styling */
.filter-content select.form-child {
    appearance: none;
    background: #fff;
    border: 1px solid #c3ced6;
    padding: .55rem .9rem .55rem .7rem;
    font-size: .75rem;
    line-height: 1.1;
    border-radius: .65rem;
    color: #13242c;
    position: relative;
    width: 100%;
    transition: .18s border-color, .18s box-shadow, .18s background;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 20 20'%3E%3Cpath fill='%23666' d='M5.516 7.548a.7.7 0 0 1 .968-.032l3.516 3.22 3.516-3.22a.7.7 0 1 1 .936 1.04l-4 3.666a.7.7 0 0 1-.936 0l-4-3.666a.7.7 0 0 1 0-1.008Z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: calc(100% - .55rem) 50%;
    background-size: 16px;
}

.filter-content select.form-child:focus {
    outline: none;
    border-color: var(--sellwerk-color-main-accent, #008fb3);
    box-shadow: 0 0 0 3px rgba(var(--sellwerk-color-main-accent-rgb, 0, 143, 179), .25);
}

.filter-content select.form-child.selected {
    border-color: var(--sellwerk-color-main-accent, #008fb3);
    background: linear-gradient(135deg, rgba(var(--sellwerk-color-main-accent-rgb, 0, 143, 179), .10), rgba(var(--sellwerk-color-main-accent-rgb, 0, 143, 179), .03));
}

.filter-content select.form-child:hover {
    background: #f5fafc;
}

.filter-content select.form-child[multiple] {
    background-image: none;
    padding: .55rem .6rem;
    height: auto;
    min-height: 7.4rem;
    line-height: 1.25;
    overflow-y: auto;
}

.filter-content select.form-child[multiple] option {
    padding: .2rem .25rem;
}

.filter-content select.form-child[multiple] option:hover {
    background: rgba(var(--sellwerk-color-main-accent-rgb, 0, 143, 179), .10);
}

.filter-content select.form-child[multiple]::-webkit-scrollbar {
    width: 8px;
}

.filter-content select.form-child[multiple]::-webkit-scrollbar-track {
    background: transparent;
}

.filter-content select.form-child[multiple]::-webkit-scrollbar-thumb {
    background: #c3ced6;
    border-radius: 4px;
}

.filter-content select.form-child[multiple]::-webkit-scrollbar-thumb:hover {
    background: #a5b2bc;
}

/* Inputs inside section-body for cohesion */
.filter-content .section-body .form-child:not(select) {
    border: 1px solid #c3ced6;
    padding: .55rem .7rem;
    font-size: .75rem;
    border-radius: .65rem;
    background: #fff;
    transition: .18s border-color, .18s box-shadow, .18s background;
}

.filter-content .section-body .form-child:not(select):focus {
    outline: none;
    border-color: var(--sellwerk-color-main-accent, #008fb3);
    box-shadow: 0 0 0 3px rgba(var(--sellwerk-color-main-accent-rgb, 0, 143, 179), .25);
}

.filter-content .section-body .form-child:not(select):hover {
    background: #f5fafc;
}

.filter-content .section-body .date-end-field:disabled {
    opacity: .5;
    color: revert;
}

/* Section headers modern look */
.filter-group .section-header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: .45rem;
    padding: .55rem .7rem;
    border-radius: .6rem;
    cursor: pointer;
    margin: .55rem 0 .4rem;
    transition: .18s background, .18s border-color, .18s box-shadow;
    /* Default neutrale Akzentfarbe, kann pro Gruppe überschrieben werden */
    --fg-accent-rgb: 148, 163, 184;
    background: rgba(var(--fg-accent-rgb), .12);
    border: 1px solid rgba(var(--fg-accent-rgb), .35);
}

.filter-group .section-header .section-accent {
    width: 0.18rem;
    height: 1.05rem;
    border-radius: 999px;
    background: rgb(var(--fg-accent-rgb));
}

.filter-group .section-header:hover {
    background: rgba(var(--fg-accent-rgb), .18);
    box-shadow: 0 0 0 2px rgba(var(--fg-accent-rgb), .18);
}

.filter-group .section-header:active {
    background: rgba(var(--fg-accent-rgb), .24);
}

.filter-group .section-header .chevron {
    width: 14px;
    height: 14px;
    display: inline-block;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 20 20'%3E%3Cpath fill='%23122' d='M5.516 7.548a.7.7 0 0 1 .968-.032l3.516 3.22 3.516-3.22a.7.7 0 1 1 .936 1.04l-4 3.666a.7.7 0 0 1-.936 0l-4-3.666a.7.7 0 0 1 0-1.008Z'/%3E%3C/svg%3E");
    background-size: 14px;
    background-repeat: no-repeat;
    transform: rotate(-90deg);
    transition: .25s transform;
}

.filter-group .section-header .chevron.open {
    transform: rotate(0deg);
}

/* Accent color mapping per filter group – aligned mit Chip-Farben */
.filter-group--contact .section-header {
    --fg-accent-rgb: 37, 99, 235; /* chip--contact */
}

.filter-group--customer .section-header {
    --fg-accent-rgb: 45, 212, 191; /* chip--customer */
}

.filter-group--segment .section-header {
    --fg-accent-rgb: 236, 72, 153; /* chip--segment */
}

.filter-group--product .section-header {
    --fg-accent-rgb: 249, 115, 22; /* chip--product */
}

.filter-group--segments .section-header {
    --fg-accent-rgb: 245, 158, 11; /* chip--segments */
}

.filter-group--productGroups.filter-group--sales .section-header,
.filter-group--sales .section-header {
    --fg-accent-rgb: 34, 197, 94; /* chip--sales */
}

/* Popup für bereits angewendeten Filter
   - sitzt innerhalb der Filter-Content-Fläche, direkt unter der Sticky-Filterbar
   - blockiert den Scroll im Container (siehe inline-style auf filter-wrapper) */
.filter-feedback-overlay {
    position: absolute;
    top: 0;
    height: 100%;
    width: 100%;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
}

.filter-feedback-backdrop {
    position: absolute;
    inset: 0;
    border-radius: 11px;
    background: rgba(15, 23, 42, 0.35);
}

.filter-feedback-modal {
    position: relative;
    max-width: 420px;
    width: 90vw;
    background: #ffffff;
    border-radius: 14px;
    box-shadow: 0 18px 45px rgba(15,23,42,0.35);
    padding: 1.5rem 1.4rem 1.2rem;
    margin: 12px;
    z-index: 1;
}

.container:has(.filter-feedback-overlay) {
    /* Wenn die Feedback-Overlay aktiv ist, soll der Container nicht scrollen */
    overflow: hidden !important;
}

.filter-feedback-title {
    margin: 0 0 0.5rem 0;
    font-size: 1.05rem;
    font-weight: 600;
    color: #0f172a;
}

.filter-feedback-text {
    margin: 0 0 1.1rem 0;
    font-size: 0.9rem;
    line-height: 1.4;
    color: #475569;
}

.filter-feedback-timer {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    margin-bottom: 0.9rem;
}

.filter-feedback-timer-bar {
    width: 100%;
    height: 4px;
    border-radius: 999px;
    background: #e5e7eb;
    overflow: hidden;
}

.filter-feedback-timer-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, #22c55e, #f59e0b);
    transition: width 0.35s linear;
}

.filter-feedback-timer-text {
    font-size: 0.8rem;
    color: #6b7280;
}

.filter-feedback-btn {
    min-width: 120px;
    border-radius: 999px;
    border: none;
    padding: 0.45rem 1rem;
    font-size: 0.85rem;
    font-weight: 600;
    background: #0f766e;
    color: #fff;
    cursor: pointer;
    box-shadow: 0 6px 18px rgba(15,118,110,0.35);
    transition: background 0.18s, box-shadow 0.18s, transform 0.08s;
}

.filter-feedback-btn:hover {
    background: #0d6b63;
    box-shadow: 0 10px 24px rgba(15,118,110,0.45);
}

.filter-feedback-btn:active {
    transform: translateY(1px);
    box-shadow: 0 4px 14px rgba(15,118,110,0.38);
}

.filter-group--potential .section-header {
    --fg-accent-rgb: 6, 182, 212; /* chip--potential */
}

.filter-group--cityZip .section-header {
    --fg-accent-rgb: 14, 165, 233; /* chip--cityZip */
}

.filter-group--sector .section-header {
    --fg-accent-rgb: 100, 116, 139; /* chip--sector */
}

.filter-group--invoice .section-header {
    --fg-accent-rgb: 225, 29, 72; /* chip--invoice */
}

.filter-group--sort .section-header {
    --fg-accent-rgb: 107, 33, 168; /* chip--sort */
}

.section-body {
    padding: 0 .25rem .5rem;
    display: grid;
    gap: .5rem;
}

/* Date range + quick picks */
.date-range {
    display: grid;
    gap: .5rem;
}

.date-range.vertical .inputs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: .5rem;
}

.date-range .quick-picks {
    display: flex;
    gap: .4rem;
}

.quick-picks .mini {
    font-size: 0.7rem;
    padding: 0.35rem 0.55rem;
    border: 1px solid #c3ced6;
    border-radius: .5rem;
    background: #fff;
    cursor: pointer;
}

.quick-picks .mini:hover {
    background: #f0f6f9;
}

.headline-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: .5rem;
}

.headline-wrapper h2 {
    margin: 0;
}

.search-toggle-bar.header {
    margin: 0;
    display: flex;
    align-items: center;
}

.search-toggle-bar.header .search-toggle {
    display: inline-flex;
    align-items: center;
}

.search-toggle.inline {
    margin-left: .5rem;
    font-size: .65rem;
    padding: .2rem .45rem;
}

/* Sort direction toggle */
.sort-direction {
    display: flex;
    align-items: center;
}

.toggle-label {
    display: flex;
    align-items: center;
    gap: .4rem;
    font-size: .75rem;
}

.toggle-label input[type="checkbox"] {
    width: 16px;
    height: 16px;
}

/* --- Produktfamilie Include/Exclude modern pills --- */
.produktkombi-include-exclude-list,
.produkte-include-exclude-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin: 0.5rem 0 0.2rem 0;
}

.produktkombi-pill-label {
    font-weight: 600;
    font-size: 0.85rem;
    color: #223;
    margin-right: 0.5rem;
}

.pill {
    border: none;
    border-radius: 999px;
    padding: 0.12rem 0.7rem;
    font-size: 0.68rem;
    font-weight: 600;
    margin-left: 0.15rem;
    cursor: pointer;
    background: #e4ebef;
    color: #223;
    transition: background 0.18s, color 0.18s, box-shadow 0.18s;
    outline: none;
}

.pill.small {
    padding: 0.08rem 0.55rem;
    font-size: 0.62rem;
}

.pill-include-toggle.active {
    background: #2d8f2d;
    color: #fff;
    box-shadow: 0 0 0 2px rgba(45, 143, 45, 0.18);
}

.pill-exclude-toggle.active {
    background: #b80000;
    color: #fff;
    box-shadow: 0 0 0 2px rgba(184, 0, 0, 0.18);
}

.pill:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.sort-direction {
    position: relative;
    display: grid;
    grid-auto-flow: column;
    grid-gap: 12px;
    gap: 12px;
    width: 100%;
}


/* --- Collapse by width styles --- */
.filter-wrapper {
    transition: width 0.3s cubic-bezier(0.4,0,0.2,1), min-width 0.3s cubic-bezier(0.4,0,0.2,1);
    overflow: hidden;
    width: 100%;
    min-width: 320px;
    position: relative;
}
.filter-wrapper.collapsed {
    width: 48px;
    min-width: 48px;
}
.filter-wrapper.collapsed .container-wrapper {
    display: none !important;
}
.collapse-filter-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 10;
    background: #f5f5f5;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 4px 10px;
    font-size: 0.95em;
    cursor: pointer;
    transition: background 0.2s;
}
.collapse-filter-btn:hover {
    background: #e0e0e0;
}
</style>
/* Toggle Switch Styles (aus SalesPitchFeedback.vue) */
// <style src="@/scss/components/search.scss" lang="scss"></style>